// Sobrellenado (coordinador; directivo en lectura).
//
// Peso real por unidad contra el peso nominal del SKU, por línea × hora × SKU
// (GET /api/coordinador/sobrellenado). Con signo: positivo = producto regalado,
// negativo = bajo peso. El costo solo valora el exceso y solo existe si el SKU
// declara costo de referencia.
//
// Regla de la pantalla: una hora sin kg se DECLARA "sin dato de peso"; nunca
// se suma como cero ni se pinta como barra en cero.

import { Navigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { SobrellenadoFila, SobrellenadoResponse } from 'schemas';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import { Icon } from '../../../components/ui/icon';
import { KpiCard } from '../../../components/ui/kpi-card';
import { Spinner } from '../../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../../components/ui/table';
import { CoordinadorSidebar } from '../../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../../components/layout/RoleLayout';
import { useSobrellenado } from '../../../lib/api/ingesta';
import { useAuth } from '../../../lib/auth';
import {
  claseTono,
  fmtConSigno,
  fmtCop,
  fmtDecimal,
  fmtGramos,
  fmtGUnidadSigno,
  fmtKgSigno,
  tonoSobrellenado,
} from '../pesoFormato';
import { errorRango, FiltroRango, useFiltrosRango } from './FiltroRango';
import { useOpcionesConectadas } from './useOpcionesConectadas';

// Diverging: exceso y faltante con dos tonos de la paleta de series (nunca
// los semánticos de alarma, que el sistema reserva para reglas).
const COLOR_EXCESO = 'var(--color-chart-2)';
const COLOR_FALTANTE = 'var(--color-chart-1)';

export interface PromedioPeso {
  gramosReal: number;
  gramosNominal: number;
  diferencia: number;
}

/** Promedio ponderado por unidades sobre las filas con peso real y nominal. */
export function promedioPeso(filas: SobrellenadoFila[]): PromedioPeso | null {
  let u = 0;
  let real = 0;
  let nominal = 0;
  for (const f of filas) {
    if (f.gramos_unidad_real === null || f.peso_nominal_g === null || f.unidades <= 0) continue;
    u += f.unidades;
    real += f.gramos_unidad_real * f.unidades;
    nominal += f.peso_nominal_g * f.unidades;
  }
  if (u === 0) return null;
  return { gramosReal: real / u, gramosNominal: nominal / u, diferencia: (real - nominal) / u };
}

export interface PuntoSobrellenado {
  clave: string;
  etiqueta: string;
  /** null = ninguna fila del punto trae peso (se declara, no se dibuja en 0). */
  kg: number | null;
}

/** Serie por día (o por hora si el rango es de un solo día). */
export function serieSobrellenado(
  filas: SobrellenadoFila[],
  porHora: boolean,
): PuntoSobrellenado[] {
  const mapa = new Map<string, PuntoSobrellenado>();
  for (const f of filas) {
    const clave = porHora ? f.hora_inicio_bloque.slice(0, 5) : f.fecha;
    const p = mapa.get(clave) ?? {
      clave,
      etiqueta: porHora ? clave : `${clave.slice(8, 10)}/${clave.slice(5, 7)}`,
      kg: null,
    };
    if (f.sobrellenado_kg !== null) p.kg = (p.kg ?? 0) + f.sobrellenado_kg;
    mapa.set(clave, p);
  }
  return [...mapa.values()].sort((a, b) => a.clave.localeCompare(b.clave));
}

export function SobrellenadoPage() {
  const { state } = useAuth();
  const [filtros, setFiltros] = useFiltrosRango();
  const opciones = useOpcionesConectadas();
  const rangoInvalido = errorRango(filtros.desde, filtros.hasta) !== null;
  const q = useSobrellenado(
    {
      desde: filtros.desde,
      hasta: filtros.hasta,
      ...(filtros.plantaId ? { planta_id: filtros.plantaId } : {}),
      ...(filtros.lineaId ? { linea_id: filtros.lineaId } : {}),
    },
    { enabled: !rangoInvalido },
  );

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Sobrellenado</h1>
            <p className="mt-1 text-[12px] text-fg-tertiary">
              Peso real por unidad frente al peso nominal del SKU, hora por hora. Positivo =
              producto regalado; negativo = bajo peso.
            </p>
          </div>
          <FiltroRango
            filtros={filtros}
            onChange={setFiltros}
            plantas={opciones.plantas}
            lineas={opciones.lineas}
          />
        </header>
        {rangoInvalido ? null : q.isLoading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-fg-secondary">
            <Spinner className="size-4" /> Cargando…
          </div>
        ) : q.isError || !q.data ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-sm text-crit">
            {q.error instanceof Error ? q.error.message : 'No pudimos cargar el sobrellenado'}
          </p>
        ) : (
          <SobrellenadoVista data={q.data} porHora={filtros.desde === filtros.hasta} />
        )}
      </div>
    </RoleLayout>
  );
}

/** Vista presentacional (KPIs + gráfica + tabla). Exportada para pruebas. */
export function SobrellenadoVista({
  data,
  porHora,
}: {
  data: SobrellenadoResponse;
  porHora: boolean;
}) {
  const { resumen, filas } = data;
  const totalHoras = resumen.horas_con_dato_peso + resumen.horas_sin_dato_peso;

  if (filas.length === 0) {
    return (
      <div className="rounded border border-dashed border-border px-4 py-10 text-center">
        <p className="text-sm font-medium text-fg">Sin producción en el período</p>
        <p className="mt-1 text-[12px] text-fg-tertiary">
          No hay horas válidas de las líneas filtradas entre esas fechas.
        </p>
      </div>
    );
  }

  if (resumen.horas_con_dato_peso === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3 rounded border border-border bg-surface-elevated px-4 py-5">
          <Icon name="scale" size={22} className="mt-0.5 text-fg-tertiary" />
          <div>
            <p className="text-sm font-medium text-fg">Sin dato de peso</p>
            <p className="mt-1 max-w-xl text-[12px] text-fg-secondary">
              Ninguna de las {totalHoras} hora{totalHoras === 1 ? '' : 's'} del período trae kilos,
              así que el sobrellenado no se puede calcular. No se reporta como cero: la línea
              necesita báscula o conteo de kilos desde la máquina.
            </p>
          </div>
        </div>
        <TablaSobrellenado filas={filas} />
      </div>
    );
  }

  const prom = promedioPeso(filas);
  const serie = serieSobrellenado(filas, porHora);
  const sobrellenadoKg = resumen.sobrellenado_kg;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid grid-cols-2 gap-y-5 py-4 lg:grid-cols-4">
          <KpiCard
            fitValue
            label="Sobrellenado"
            hint="Suma con signo de (kg reales − kg nominales). Negativo = bajo peso."
            value={sobrellenadoKg === null ? 'Sin dato' : fmtConSigno(sobrellenadoKg)}
            valueSuffix={sobrellenadoKg === null ? undefined : 'kg'}
            sub={`${resumen.kg === null ? '—' : fmtDecimal(resumen.kg)} kg producidos`}
          />
          <KpiCard
            fitValue
            label="Peso por unidad"
            hint="Promedio ponderado por unidades, solo horas con kilos y SKU con peso nominal."
            value={prom ? fmtDecimal(prom.gramosReal) : 'Sin dato'}
            valueSuffix={prom ? 'g' : undefined}
            sub={
              prom
                ? `nominal ${fmtGramos(prom.gramosNominal)} · ${fmtGUnidadSigno(prom.diferencia)}`
                : 'SKU sin peso nominal'
            }
            subTone={prom ? tonoSobrellenado(prom.diferencia) : 'neutral'}
            divider
          />
          {resumen.costo_sobrellenado_cop !== null ? (
            <KpiCard
              fitValue
              label="Costo del exceso"
              hint="Solo el exceso (no el bajo peso) × costo de referencia por kg del SKU."
              value={fmtCop(resumen.costo_sobrellenado_cop)}
              sub="producto regalado"
              divider
            />
          ) : null}
          <KpiCard
            fitValue
            label="Horas sin dato de peso"
            value={resumen.horas_sin_dato_peso}
            sub={`de ${totalHoras} horas · no se cuentan como cero`}
            subTone={resumen.horas_sin_dato_peso > 0 ? 'warn' : 'neutral'}
            divider
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-2 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-medium text-fg">
              Sobrellenado por {porHora ? 'hora' : 'día'} (kg)
            </h2>
            <div className="flex items-center gap-4 text-[11px] text-fg-secondary">
              <Leyenda color={COLOR_EXCESO} label="Exceso" />
              <Leyenda color={COLOR_FALTANTE} label="Bajo peso" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={serie} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="etiqueta"
                tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => fmtDecimal(Number(v))}
                tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
                width={52}
              />
              <ReferenceLine y={0} stroke="var(--color-border-stronger)" />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)' }}
                contentStyle={{
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 4,
                  fontSize: 12,
                  color: 'var(--color-fg)',
                }}
                formatter={(v) =>
                  v === null || v === undefined
                    ? ['Sin dato de peso', '']
                    : [fmtKgSigno(Number(v)), 'Sobrellenado']
                }
              />
              <Bar dataKey="kg" radius={[4, 4, 4, 4]} maxBarSize={36}>
                {serie.map((p) => (
                  <Cell key={p.clave} fill={(p.kg ?? 0) < 0 ? COLOR_FALTANTE : COLOR_EXCESO} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {serie.some((p) => p.kg === null) ? (
            <p className="text-[11px] text-fg-tertiary">
              Los {porHora ? 'horas' : 'días'} sin barra no tienen dato de peso.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <TablaSobrellenado filas={filas} />
    </div>
  );
}

function Leyenda({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="size-2 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

function TablaSobrellenado({ filas }: { filas: SobrellenadoFila[] }) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table maxHeight="60vh">
          <THead sticky>
            <TR>
              <TH>Fecha · hora</TH>
              <TH>Línea · SKU</TH>
              <TH align="right">Unidades</TH>
              <TH align="right">kg</TH>
              <TH align="right">g/u real</TH>
              <TH align="right">Nominal</TH>
              <TH align="right">Diferencia</TH>
              <TH align="right">Sobrellenado</TH>
              <TH align="right">Costo</TH>
            </TR>
          </THead>
          <TBody>
            {filas.map((f, i) => {
              const tono = tonoSobrellenado(f.sobrellenado_g_unidad, f.tolerancia_g);
              return (
                <TR key={`${f.linea_id}-${f.fecha}-${f.hora_inicio_bloque}-${f.sku_id}-${i}`}>
                  <TD className="whitespace-nowrap tabular-nums text-fg-secondary">
                    {f.fecha} · {f.hora_inicio_bloque.slice(0, 5)}
                  </TD>
                  <TD>
                    <p className="text-sm text-fg">{f.linea_nombre}</p>
                    <p className="text-[11px] text-fg-tertiary">{f.sku_descripcion}</p>
                  </TD>
                  <TD align="right" numeric className="text-fg-secondary">
                    {f.unidades.toLocaleString('es-CO')}
                  </TD>
                  {f.sin_dato_peso ? (
                    <TD colSpan={6} align="right">
                      <Badge tone="neutral" className="normal-case tracking-normal">
                        Sin dato de peso
                      </Badge>
                    </TD>
                  ) : (
                    <>
                      <TD align="right" numeric className="text-fg-secondary">
                        {f.kg === null ? '—' : fmtDecimal(f.kg)}
                      </TD>
                      <TD align="right" numeric className="text-fg">
                        {fmtGramos(f.gramos_unidad_real)}
                      </TD>
                      <TD align="right" numeric className="text-fg-secondary">
                        {f.peso_nominal_g === null ? (
                          <span className="text-[11px] text-fg-tertiary">sin nominal</span>
                        ) : (
                          <>
                            {fmtGramos(f.peso_nominal_g)}
                            {f.tolerancia_g !== null ? (
                              <span className="text-[11px] text-fg-tertiary">
                                {' '}
                                ±{fmtDecimal(f.tolerancia_g)}
                              </span>
                            ) : null}
                          </>
                        )}
                      </TD>
                      <TD align="right" numeric className={claseTono(tono)}>
                        {fmtGUnidadSigno(f.sobrellenado_g_unidad)}
                      </TD>
                      <TD align="right" numeric className={claseTono(tono)}>
                        {fmtKgSigno(f.sobrellenado_kg)}
                      </TD>
                      <TD align="right" numeric className="text-fg-secondary">
                        {fmtCop(f.costo_sobrellenado_cop)}
                      </TD>
                    </>
                  )}
                </TR>
              );
            })}
          </TBody>
        </Table>
      </CardContent>
    </Card>
  );
}
